// Set up a new Smartify device: find it, then hand it the Wi-Fi it should join.
//
// ── Which radio to scan first: it depends on the HARDWARE, and both answers
//    have been wrong here at different times ────────────────────────────────
//
// The Flutter app looks for the device's own Wi-Fi hotspot — addDeviceScreen.dart
// runs a radar over `WiFiScan` for SSIDs matching `smartify<digits>`, where the
// digits ARE the unique number. Bluetooth is the SECOND path there
// (bleScanScreen.dart), offered when the hotspot does not appear. This screen
// once scanned Bluetooth only, so a user with that hardware sat watching a
// Bluetooth scan find nothing while the device advertised a Wi-Fi AP the whole
// time. Wi-Fi was made the default to fix it.
//
// ⚠️ AND THAT IS NOW WRONG FOR THE CURRENT HARDWARE, for the mirror-image
// reason. The ESP8684 (ESP32-C2) firmware is STA-ONLY — it never raises an AP.
// Its own CLAUDE.md is explicit: "no SoftAP, and no web UI"; provisioning is
// serial console or BLE, nothing else. A new ESP8684 therefore CANNOT appear in
// the Wi-Fi radar, and defaulting to it reproduced the original bug exactly,
// only with the radios swapped.
//
// So the default follows the radio that can actually see BOTH device families:
// Bluetooth where the platform has it, Wi-Fi radar where it does not (and as a
// one-tap fallback everywhere, since the older AP-based hardware is still out
// there and only the hotspot path reaches it).
//
// The two paths differ only in how the credentials travel:
//   hotspot — the phone must be joined to the device's AP, then the device is
//             given the network at http://192.168.4.1/add?ssid=…&pass=…, which
//             is exactly what the Dart does (addDeciceFuncions.dart registerWiFi)
//   BLE     — written to the provisioning characteristic (core/ble/provisionWifi)
//
// Either way the credentials themselves are chosen by WifiPickerDialog, so the
// networks already known for the location are one tap.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Button, CircularProgress, List, ListItemButton,
  ListItemIcon, ListItemText, Divider, IconButton, useTheme,
} from '@mui/material';
import BluetoothSearchingIcon from '@mui/icons-material/BluetoothSearching';
import WifiTetheringIcon from '@mui/icons-material/WifiTethering';
import RouterIcon from '@mui/icons-material/Router';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useLocation } from 'react-router-dom';
import { CenterTitleBar } from '../components/MobileAppBar';
import {
  bleSupported, scanForDevices, stopScan,
  isBleEnabled, requestBleEnable, watchBleEnabled, openBleSettings,
} from '../../../core/ble/bleClient';
import BluetoothOffBanner from '../components/BluetoothOffBanner';
import { provisionWifiOverBle } from '../../../core/ble/provisionWifi';
import {
  scanNetworks, wifiScanSupported, partitionNetworks, unoFromHotspot,
  WIFI_OFF, PERMISSION_DENIED, PLUGIN_MISSING,
} from '../../../core/wifi/wifiScan';
import WifiPickerDialog from '../components/WifiPickerDialog';

// Where a device in setup mode serves its provisioning form. Fixed in the
// firmware; the same address the Dart opens.
const SETUP_URL = 'http://192.168.4.1/add';

export default function WifiOnboardScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const { search } = useLocation();
  const locationId = new URLSearchParams(search).get('locationId') || null;

  // 'ble' — reaches every current device. 'wifi' — the hotspot radar, the only
  // path to the older AP-based hardware. See the note at the top of the file.
  // Computed once via the lazy initialiser: bleSupported() is a platform check,
  // not state, and re-running it every render buys nothing.
  const [mode, setMode] = useState(() => (bleSupported() ? 'ble' : 'wifi'));

  const [hotspots, setHotspots] = useState([]);
  const [wifiScanning, setWifiScanning] = useState(false);
  const [wifiError, setWifiError] = useState('');

  const [bleDevices, setBleDevices] = useState([]);
  const [bleScanning, setBleScanning] = useState(false);
  const [bleError, setBleError] = useState('');

  // null until asked — see the banner's guard. `false` is what draws it.
  const [btOn, setBtOn] = useState(null);

  const [target, setTarget] = useState(null);   // { kind, label, uno?, deviceId? }
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [result, setResult] = useState(null);
  const aliveRef = useRef(true);

  useEffect(() => () => { aliveRef.current = false; stopScan(); }, []);

  const radar = useCallback(async () => {
    if (!wifiScanSupported()) {
      setWifiError('Finding a new device needs the installed app, not a browser.');
      return;
    }
    setWifiScanning(true);
    setWifiError('');
    try {
      const nets = await scanNetworks();
      if (!aliveRef.current) return;
      setHotspots(partitionNetworks(nets).setup);
    } catch (e) {
      if (!aliveRef.current) return;
      setWifiError(
        e.code === WIFI_OFF ? 'Turn Wi-Fi on so the app can find the device.'
          : e.code === PERMISSION_DENIED
            ? 'Permission is needed to look for the device’s hotspot.'
            // On this screen the plugin IS the feature — there is no reduced
            // version to fall back to, so say so plainly and point at Bluetooth,
            // which needs nothing new.
            : e.code === PLUGIN_MISSING
              ? 'This version of the app cannot search for a device’s hotspot. '
                + 'Update from the Play Store, or use the Bluetooth search below.'
              : 'Could not scan for devices. Try again.',
      );
    } finally {
      if (aliveRef.current) setWifiScanning(false);
    }
  }, []);

  const bleScan = useCallback(async () => {
    // ⚠️ THIS IS THE ONE A CUSTOMER HITS. With the radio off, the scan resolves
    // and finds nothing — so onboarding a brand-new device would sit on "no
    // devices found" and the obvious conclusion is that the device is faulty.
    // The banner below the button is what turns that into a fixable answer.
    if (!(await isBleEnabled())) {
      if (!aliveRef.current) return;
      setBtOn(false);
      setBleDevices([]);
      setBleError('Bluetooth is off — turn it on to find a new device.');
      return;
    }
    setBleDevices([]);
    setBleError('');
    setBleScanning(true);
    try {
      await scanForDevices((d) => {
        if (!aliveRef.current) return;
        // MERGE, don't ignore -- see the note in BleTestScreen. A device whose
        // name arrives late is emitted twice, and the second one carries the
        // uniqueNumber this screen provisions against.
        setBleDevices((prev) => {
          const i = prev.findIndex((x) => x.deviceId === d.deviceId);
          if (i < 0) return [...prev, d];
          const next = prev.slice();
          next[i] = { ...next[i], ...d };
          return next;
        });
      });
    } catch (e) {
      if (aliveRef.current) setBleError((e && e.message) || 'Bluetooth scan failed');
    } finally {
      if (aliveRef.current) setBleScanning(false);
    }
  }, []);

  // Scan on open, on whichever radio `mode` defaulted to. Deliberately not
  // depending on the scan callbacks' identity — they change when their own
  // state flips, which would restart the scan the moment one finished.
  useEffect(() => {
    if (bleSupported()) bleScan(); else radar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Watched, not checked once: the user turns Bluetooth on from the quick
  // settings after reading the banner, and the screen has to notice by itself —
  // a re-scan they have to discover is most of the original problem again.
  useEffect(() => {
    if (!bleSupported()) return undefined;
    return watchBleEnabled((on) => {
      if (!aliveRef.current) return;
      setBtOn(on);
      // Came on while this screen was open: start the scan they came here for.
      if (on) { setBleError(''); bleScan(); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const turnOnBluetooth = async () => {
    const on = await requestBleEnable();
    setBtOn(on);
    if (on) { setBleError(''); bleScan(); }
  };

  const pick = (t) => { setTarget(t); setPickerOpen(true); };

  const send = async ({ ssid, pass }) => {
    setPickerOpen(false);
    if (target?.kind === 'ble') {
      setBusy(true);
      setResult(null);
      await stopScan();
      // The unique number is not decoration here — the BLE auth key is derived
      // from it, so a device whose advertised name did not parse cannot be
      // authenticated and must not be attempted.
      const r = await provisionWifiOverBle(
        target.deviceId, target.uno, ssid, pass, setProgress,
      );
      if (!aliveRef.current) return;
      setResult(r);
      setProgress('');
      setBusy(false);
      return;
    }
    // Hotspot path. The device serves the form itself, so opening it shows the
    // device's own confirmation — the app cannot fake that, and the user needs
    // to see it to know the device accepted.
    const url = `${SETUP_URL}?ssid=${encodeURIComponent(ssid)}&pass=${encodeURIComponent(pass)}`;
    window.open(url, '_blank');
    setResult({
      ok: true,
      message: 'The device’s setup page was opened. It restarts once it accepts '
        + 'the network — wait about 20 seconds, then come back.',
    });
  };

  const heading = (icon, text, action) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
      {icon}
      <Typography sx={{ color: c.text, fontWeight: 700, flex: 1 }}>{text}</Typography>
      {action}
    </Box>
  );

  return (
    <Box sx={{ height: '100%', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <CenterTitleBar
        title="Set Up New Device"
        showBack
        actions={(
          <IconButton
            onClick={mode === 'wifi' ? radar : bleScan}
            disabled={wifiScanning || bleScanning || busy}
            sx={{ color: c.text }}
          >
            <RefreshIcon />
          </IconButton>
        )}
      />

      {/* Above the scan results, and only while a result is not being shown —
          a provisioning outcome is its own screen. `false` only: `null` means
          the radio has not been asked about yet. */}
      {btOn === false && !result && !busy && (
        <BluetoothOffBanner onTurnOn={turnOnBluetooth} onOpenSettings={openBleSettings} />
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
        <Box sx={{ maxWidth: 480, mx: 'auto' }}>

          {result ? (
            <Box sx={{ textAlign: 'center', pt: 4 }}>
              {result.ok
                ? <CheckCircleIcon sx={{ fontSize: 72, color: '#4CAF50' }} />
                : <ErrorOutlineIcon sx={{ fontSize: 72, color: '#F44336' }} />}
              <Typography sx={{ color: c.text, fontSize: 20, fontWeight: 700, mt: 2 }}>
                {result.ok ? 'Wi-Fi Sent' : 'Setup Failed'}
              </Typography>
              <Typography sx={{ color: `${c.text}B3`, mt: 1.5 }}>{result.message}</Typography>
              {result.ok && (
                <Typography sx={{ color: `${c.text}B3`, mt: 2, fontSize: 13 }}>
                  Add it to a room with Add Device once it has joined.
                </Typography>
              )}
              <Button
                onClick={() => {
                  setResult(null);
                  setTarget(null);
                  // Back to the same radio the screen opens on, not always Wi-Fi
                  // — otherwise setting up a second ESP8684 lands on a radar
                  // that cannot see it.
                  if (bleSupported()) { setMode('ble'); bleScan(); } else { setMode('wifi'); radar(); }
                }}
                sx={{
                  mt: 4, bgcolor: c.button, color: c.text, px: 3, py: 1.25,
                  borderRadius: '12px', textTransform: 'none',
                  '&:hover': { bgcolor: c.button },
                }}
              >
                Set up another device
              </Button>
            </Box>
          ) : busy ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pt: 4 }}>
              <CircularProgress size={20} sx={{ color: c.text }} />
              <Typography sx={{ color: c.text }}>{progress || 'Working…'}</Typography>
            </Box>
          ) : mode === 'wifi' ? (
            <>
              {heading(
                <WifiTetheringIcon sx={{ color: c.text }} />,
                wifiScanning ? 'Looking for devices…' : `${hotspots.length} device${hotspots.length === 1 ? '' : 's'} found`,
                wifiScanning ? <CircularProgress size={16} sx={{ color: c.text }} /> : null,
              )}
              <Typography sx={{ color: `${c.text}B3`, fontSize: 13, mb: 1.5 }}>
                Power the new device on and keep it close. It puts out its own
                Wi-Fi named “Smartify…”. Join that network in your phone’s Wi-Fi
                settings before sending the credentials.
              </Typography>

              {wifiError && (
                <Typography sx={{ color: '#F44336', mb: 2 }}>{wifiError}</Typography>
              )}

              <Divider sx={{ borderColor: `${c.text}33` }} />
              <List>
                {hotspots.map((h) => (
                  <ListItemButton
                    key={h.ssid}
                    onClick={() => pick({ kind: 'hotspot', label: h.ssid, uno: unoFromHotspot(h.ssid) })}
                  >
                    <ListItemIcon sx={{ color: c.text, minWidth: 40 }}><RouterIcon /></ListItemIcon>
                    <ListItemText
                      primary={h.ssid}
                      secondary={`Unique number ${unoFromHotspot(h.ssid) ?? '—'}`}
                      primaryTypographyProps={{ sx: { color: c.text, fontWeight: 600 } }}
                      secondaryTypographyProps={{ sx: { color: `${c.text}99`, fontSize: 12 } }}
                    />
                  </ListItemButton>
                ))}
              </List>

              {!wifiScanning && hotspots.length === 0 && !wifiError && (
                <Typography sx={{ color: `${c.text}B3`, textAlign: 'center', mt: 3 }}>
                  No devices in setup mode nearby. Check the device is powered on
                  and not already set up, then scan again.
                </Typography>
              )}

              {bleSupported() && (
                <Button
                  fullWidth
                  startIcon={<BluetoothSearchingIcon />}
                  onClick={() => { setMode('ble'); bleScan(); }}
                  sx={{ mt: 3, color: c.text, textTransform: 'none' }}
                >
                  Back to Bluetooth search
                </Button>
              )}
            </>
          ) : (
            <>
              {heading(
                <BluetoothSearchingIcon sx={{ color: c.text }} />,
                bleScanning ? 'Scanning…' : 'Nearby devices',
                bleScanning ? <CircularProgress size={16} sx={{ color: c.text }} /> : null,
              )}
              <Typography sx={{ color: `${c.text}B3`, fontSize: 13, mb: 1.5 }}>
                Power the new device on and keep it close. It appears as
                “Smartify…” followed by its unique number.
              </Typography>

              {bleError && <Typography sx={{ color: '#F44336', mb: 2 }}>{bleError}</Typography>}

              <Divider sx={{ borderColor: `${c.text}33` }} />
              <List>
                {bleDevices.map((d) => (
                  <ListItemButton
                    key={d.deviceId}
                    onClick={() => pick({
                      kind: 'ble', label: d.name, deviceId: d.deviceId, uno: d.uniqueNumber,
                    })}
                  >
                    <ListItemIcon sx={{ color: c.text, minWidth: 40 }}><RouterIcon /></ListItemIcon>
                    <ListItemText
                      primary={d.uniqueNumber || d.name}
                      secondary={[
                        // "assigned" is read from the advertisement, so the list
                        // can say whose a device is before connecting to it.
                        d.service ? (d.service.assigned ? 'Already claimed' : 'New device') : null,
                        d.rssi != null ? `Signal ${d.rssi} dBm` : null,
                      ].filter(Boolean).join(' · ')}
                      primaryTypographyProps={{ sx: { color: c.text, fontWeight: 600 } }}
                      secondaryTypographyProps={{ sx: { color: `${c.text}99`, fontSize: 12 } }}
                    />
                  </ListItemButton>
                ))}
              </List>

              {!bleScanning && bleDevices.length === 0 && !bleError && (
                <Typography sx={{ color: `${c.text}B3`, textAlign: 'center', mt: 3 }}>
                  No devices found over Bluetooth. Check the device is powered on
                  and not already set up, then scan again.
                </Typography>
              )}

              <Button
                fullWidth
                startIcon={<WifiTetheringIcon />}
                onClick={() => { stopScan(); setMode('wifi'); radar(); }}
                sx={{ mt: 3, color: c.text, textTransform: 'none' }}
              >
                Older device? Search for its Wi-Fi hotspot
              </Button>
            </>
          )}
        </Box>
      </Box>

      <WifiPickerDialog
        open={pickerOpen}
        locationId={locationId}
        deviceName={target?.label}
        submitLabel="Send Wi-Fi"
        onClose={() => setPickerOpen(false)}
        onSubmit={send}
      />
    </Box>
  );
}
